import { useEffect, useRef } from "react";

/**
 * إبطال البيانات بعد أي تعديل (بديل invalidateQueries).
 *
 * كل طلب ناجح يغيّر بيانات (POST / PUT / PATCH / DELETE) يُعلن «تغيّرت البيانات»،
 * فتعيد الشاشات المفتوحة — في هذا التبويب وفي التبويبات الأخرى — جلب بياناتها من
 * الخادم مباشرة. لا حاجة لتحديث الصفحة أو إعادة الدخول أو إعادة البحث.
 */
const EVENT = "asf:data-changed";
let channel = null;
try {
  channel = typeof BroadcastChannel !== "undefined" ? new BroadcastChannel("asf-data") : null;
} catch {
  channel = null;
}

let timer = null;
let pending = new Set();

export function announceDataChange(url = "") {
  pending.add(String(url).split("?")[0]);
  clearTimeout(timer);
  // عدة طلبات متتالية (حفظ ثم رفع مرفق مثلاً) تُجمع في إعلان واحد
  timer = setTimeout(() => {
    const detail = { urls: [...pending], at: Date.now() };
    pending = new Set();
    window.dispatchEvent(new CustomEvent(EVENT, { detail }));
    try {
      channel?.postMessage(detail);
    } catch {
      /* تبويب مغلق */
    }
  }, 150);
}

if (channel) {
  channel.onmessage = (e) => window.dispatchEvent(new CustomEvent(EVENT, { detail: e.data }));
}

/** طلبات القراءة الثقيلة التي لا تُعد تعديلاً للبيانات رغم أنها POST. */
const READ_ONLY_POSTS = [/WorkOrderFlow\/(search|export|board|baskets\/work-orders|baskets\/export)$/i, /Monitoring\/board$/i, /DailyWork\/(board|today|export)$/i, /TypedWorkOrders\/[^/]+\/price-items\/lookup$/i, /pdc\/(dashboard|work-orders|export\/(excel|pdf))$/i, /internal-indicators\/(dashboard|work-orders|export\/(excel|pdf))$/i, /Account\/login/i, /\/import$/i,
  // الاستيراد الذكي من Excel: التحليل والتحقق لا يغيّران أوامر العمل (الإنشاء commit فقط)
  /WorkOrderExcelImport\/(analyze|\d+\/(sheet|validate|values\/approve|cancel))$/i];

export function isMutation(config) {
  const method = String(config?.method || "get").toLowerCase();
  if (!["post", "put", "patch", "delete"].includes(method)) return false;
  const url = String(config?.url || "");
  if (method === "post" && READ_ONLY_POSTS.some((r) => r.test(url.split("?")[0]))) {
    // الاستيراد بلا تطبيق معاينة فقط
    if (/\/import$/i.test(url.split("?")[0])) {
      try {
        const body = typeof config.data === "string" ? JSON.parse(config.data) : config.data;
        return !!body?.apply;
      } catch {
        return false;
      }
    }
    return false;
  }
  return true;
}

/**
 * يعيد تشغيل reload عند أي تغيير في البيانات، وعند عودة المستخدم إلى التبويب.
 * الشاشة تُبقي فلاترها كما هي؛ النتيجة فقط تُعاد من الخادم.
 */
export function useDataRefresh(reload, { onFocus = true } = {}) {
  const ref = useRef(reload);
  ref.current = reload;

  useEffect(() => {
    const run = () => ref.current?.();
    const onVisible = () => {
      if (document.visibilityState === "visible") run();
    };
    window.addEventListener(EVENT, run);
    if (onFocus) document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener(EVENT, run);
      if (onFocus) document.removeEventListener("visibilitychange", onVisible);
    };
  }, [onFocus]);
}
